import type { ContactTitle } from './contact.js';

// Bulk Contact Upload — wire shapes shared by the client (which extracts raw
// rows from the uploaded xlsx/CSV and renders the preview) and the server
// (which owns cleanup, validation, resolution, and the writes). See
// docs/BulkContactUpload_Spec.md.
//
// NOTE on the data model: the spec talks about separate "AccountContact" docs,
// but the real schema embeds links on the Contact (`Contact.accountLinks[]`,
// see src/types/contact.ts). A "link" here is one entry in that array, keyed
// by accountId, with fields receivesServiceCommunications /
// receivesBillingCommunications / receivesCampaignPreviews / isPrimary /
// jobTitle. There is no separate collection and no per-link timestamps.

// One row exactly as read from the file, client-side. Every value is the cell
// stringified (numbers included). The server owns all transforms so the rules
// stay testable + authoritative.
export type BulkContactRawRow = {
  title: string;
  firstName: string;
  lastName: string;
  brand: string;
  group: string;
  suburb: string;
  phone: string;
  email: string;
  jobTitle: string;
  commEmail: string;
  commSMS: string;
  campaignPreviews: string;
  primaryContact: string;
  serviceComms: string;
  billingComms: string;
};

export const BULK_CONTACT_RAW_FIELDS = [
  'title',
  'firstName',
  'lastName',
  'brand',
  'group',
  'suburb',
  'phone',
  'email',
  'jobTitle',
  'commEmail',
  'commSMS',
  'campaignPreviews',
  'primaryContact',
  'serviceComms',
  'billingComms',
] as const satisfies ReadonlyArray<keyof BulkContactRawRow>;

// Header labels (as they appear in §1.1 and the downloadable template), mapped
// to the raw-row field key. The client matches sheet/CSV headers against these
// case-insensitively (whitespace-collapsed). Shared so the template generator
// and the header matcher stay in sync.
export const BULK_CONTACT_COLUMN_LABELS: Record<keyof BulkContactRawRow, string> = {
  title: 'Title',
  firstName: 'First Name',
  lastName: 'Last Name',
  brand: 'Brand',
  group: 'Group',
  suburb: 'Suburb',
  phone: 'Phone',
  email: 'Email',
  jobTitle: 'Job Title',
  commEmail: 'Comm Email',
  commSMS: 'Comm SMS',
  campaignPreviews: 'Campaign Previews',
  primaryContact: 'Primary Contact',
  serviceComms: 'Service Comms',
  billingComms: 'Billing Comms',
};

// Required column HEADERS (§1.1). A file missing any of these is rejected
// before per-row processing. (Per-row required *values* are handled by the
// validator.)
export const BULK_CONTACT_REQUIRED_COLUMNS: Array<keyof BulkContactRawRow> = [
  'firstName',
  'brand',
  'suburb',
  'email',
  'commEmail',
  'commSMS',
  'campaignPreviews',
  'primaryContact',
  'serviceComms',
  'billingComms',
];

// After §2 cleanup. Booleans are concrete (invalid cells default to false here
// but are flagged as errors by the validator, so such rows never commit).
export type CleanedBulkContactRow = {
  title: ContactTitle | null;
  firstName: string;
  lastName: string | null;
  brand: string;
  group: string | null;
  suburbs: string[]; // comma-split, trimmed, empties dropped
  phone: string | null; // E.164 when normalisable, else cleaned raw (see warnings)
  email: string; // trimmed + lowercased ('' when blank)
  jobTitle: string | null;
  channelEmail: boolean;
  channelSMS: boolean;
  receivesCampaignPreviews: boolean;
  isPrimary: boolean;
  receivesServiceCommunications: boolean;
  receivesBillingCommunications: boolean;
};

export type AccountRef = { id: string; name: string };

export type SuburbResolutionStatus = 'exact' | 'ambiguous' | 'fuzzy' | 'unmatched';

export type SuburbCandidate = { id: string; name: string; score: number };

export type SuburbResolution = {
  input: string;
  status: SuburbResolutionStatus;
  candidates: SuburbCandidate[];
  // The location the row will link to. Auto-filled for 'exact'; null for
  // 'fuzzy' / 'ambiguous' / 'unmatched' until the user confirms in the preview.
  selected: AccountRef | null;
};

export type ContactAction = 'create' | 'update';

export type ContactResolution = { id: string | null; action: ContactAction };

export type RowStatus = 'ready' | 'needs_resolution' | 'error';

export type ResolvedBulkContactRow = {
  rowNumber: number; // 1-based row index within the data rows (header excluded)
  status: RowStatus;
  raw: BulkContactRawRow;
  cleaned: CleanedBulkContactRow;
  resolved: {
    brand: AccountRef | null;
    group: AccountRef | null;
    contact: ContactResolution | null;
    suburbs: SuburbResolution[];
  };
  errors: string[];
  warnings: string[];
};

export type BulkContactParseSummary = {
  totalRows: number;
  ready: number;
  needsResolution: number;
  errors: number;
  contactsToCreate: number;
  contactsToUpdate: number;
  linksToCreate: number;
  linksToUpdate: number;
};

export type BulkContactParseRequest = { rows: BulkContactRawRow[] };

export type BulkContactParseResponse = {
  rows: ResolvedBulkContactRow[];
  summary: BulkContactParseSummary;
};

// Commit contract: the client sends the raw row (server re-cleans +
// re-validates + re-resolves brand/group — server stays authoritative) plus
// the location account ids the user confirmed for each suburb. Skipped suburbs
// are simply omitted. The server validates every selected id is a location
// under the resolved brand before writing.
export type BulkContactCommitRow = {
  rowNumber: number;
  raw: BulkContactRawRow;
  selectedSuburbAccountIds: string[];
};

export type BulkContactCommitRequest = { rows: BulkContactCommitRow[] };

export type BulkContactCommitRowError = {
  rowNumber: number;
  email: string;
  error: string;
};

export type BulkContactCommitResult = {
  created: number;
  updated: number;
  linksCreated: number;
  linksUpdated: number;
  skipped: number;
  errors: BulkContactCommitRowError[];
};
